import React, { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useInView } from 'react-intersection-observer';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import * as StyledComponents from './StyledComponents';
import EventCard from './EventCard';
import { EventData } from '../types';

interface EventDateRowProps {
  rowDate: string;
  dateEvents: EventData[];
  todayKey: string;
  tomorrowKey: string;
}

const EventDateRow: React.FC<EventDateRowProps> = ({ rowDate, dateEvents, todayKey, tomorrowKey }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '300px 0px',
  });

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const container = document.getElementById(`event-grid-${rowDate}`) as HTMLDivElement | null;
    if (!container) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const atStart = Math.floor(container.scrollLeft) <= 0;
    const atEnd = Math.ceil(container.scrollLeft) >= maxScrollLeft;

    setCanScrollLeft(!atStart);
    setCanScrollRight(!atEnd);
  }, [rowDate]);

  useEffect(() => {
    const container = document.getElementById(`event-grid-${rowDate}`) as HTMLDivElement | null;
    if (!container) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const handleScroll = () => {
      updateScrollState();
    };

    updateScrollState();

    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [rowDate, updateScrollState, dateEvents.length]);

  useEffect(() => {
    updateScrollState();
  }, [updateScrollState, dateEvents.length]);

  const handleHorizontalScroll = (direction: 'left' | 'right', currentRowDate: string) => {
    const container = document.getElementById(`event-grid-${currentRowDate}`) as HTMLDivElement | null;
    if (container) {
      const scrollAmount = direction === 'left' ? -700 : 700;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <>
      <StyledComponents.EventRowHeader>
        <h2>
          {rowDate === todayKey
            ? 'Hoy'
            : rowDate === tomorrowKey
            ? 'Mañana'
            : format(parseISO(rowDate), "EEEE d 'de' MMMM", { locale: es })}
        </h2>
        <StyledComponents.EventRowActions>
          <StyledComponents.ScrollArrow
            disabled={!canScrollLeft}
            onClick={() => handleHorizontalScroll('left', rowDate)}
          >
            <IoChevronBack size={24} />
          </StyledComponents.ScrollArrow>
          <StyledComponents.ScrollArrow
            disabled={!canScrollRight}
            onClick={() => handleHorizontalScroll('right', rowDate)}
          >
            <IoChevronForward size={24} />
          </StyledComponents.ScrollArrow>
        </StyledComponents.EventRowActions>
      </StyledComponents.EventRowHeader>
      <StyledComponents.EventGridWrapper ref={ref}>
        <StyledComponents.EventGrid id={`event-grid-${rowDate}`}>
          {dateEvents.map((event) => (
            <EventCard 
              key={`${rowDate}-${event.id}`} 
              event={event}
              isRowVisible={inView}
              eventGridId={`event-grid-${rowDate}`}
            />
          ))}
        </StyledComponents.EventGrid>
      </StyledComponents.EventGridWrapper>
    </>
  );
};

export default EventDateRow; 